import { Injectable } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome';
import { AbstractContentfulStateService, IContentfulFunction, IContentfulFunctionParameter } from '@y42/contentful';
import { TypescriptTypeEnum } from '@y42/monaco-typescript-types';
import { Entry } from 'contentful';
import sortBy from 'lodash/sortBy';
import { map, Observable } from 'rxjs';
import { ColumnTypeToTypescriptTypeConverter } from './helpers/column-to-typescript-type-converter';
import { FunctionDefinitionModel, FunctionParameter } from './models/function-definition.model';

@Injectable({
  providedIn: 'root',
})
export class MonacoContentfulFunctionsState extends AbstractContentfulStateService<
  IContentfulFunction,
  IContentfulFunction
> {
  constructor() {
    super('blendFunctions', '', 1000, (item: Entry<IContentfulFunction>) => item.fields, 2);
  }

  selectFullMonacoFunctions$: Observable<FunctionDefinitionModel[]> = this.select((s) => s.entityState).pipe(
    map((contentfulFunctionDescriptions) =>
      (contentfulFunctionDescriptions || [])
        .filter((f) => f.active)
        .map((functionDescription) => {
          let functionParameters: FunctionParameter[] = [];
          if (functionDescription.parameters) {
            functionParameters = functionDescription.parameters.map((p) => ({
              function_name: p.fields.functionId,
              name: p.fields.name,
              description: p.fields.description,
              index: p.fields.index,
              required: p.fields.required,
              typescript_type: p.fields.typescriptTypes.join(' | '),
              enum_values: p.fields.enumValues,
              min_number: p.fields?.minValue,
              max_number: p.fields?.maxValue,
              exclude_values: p.fields.excludeValues ? p.fields.excludeValues.split(',') : [],
              allow_multiple_parameters: p.fields?.allowMultipleParameters,
            }));
            functionParameters = sortBy(functionParameters, (o) => o.index);
          }

          const functionType = functionDescription.baseFunction?.fields.output?.fields.type || '';

          return {
            function_name: functionDescription.title,
            description: functionDescription.description,
            tag: functionDescription.tag?.fields?.tagName,
            tag_fontawesome_logo: functionDescription.tag?.fields?.tagFontawesomeIcon as IconProp,
            parameters: functionParameters ? functionParameters : [],
            output: functionType,
            output_type_typescript: ColumnTypeToTypescriptTypeConverter(functionType),
            requires_same_parameter_type: this.isSameTypeRequired(functionDescription.parameters),
            example: functionDescription.example,
            operator: functionDescription.operator,
          } as FunctionDefinitionModel;
        }),
    ),
    map((functions) => this.applyCustomSortOnFinalArray(functions)),
  );

  selectMonacoFunctions(showAggregateFunctions = false): Observable<FunctionDefinitionModel[]> {
    return this.selectFullMonacoFunctions$.pipe(
      map((funcDefinitions: FunctionDefinitionModel[]) =>
        showAggregateFunctions ? funcDefinitions : funcDefinitions.filter((f) => f.tag !== 'aggregation'),
      ),
    );
  }

  private isSameTypeRequired(parameters: Array<Entry<IContentfulFunctionParameter>>) {
    if (parameters) {
      const fieldTypescriptTypes: TypescriptTypeEnum[] = [];
      parameters.forEach((parameter) => {
        parameter.fields.typescriptTypes.forEach((type) => {
          fieldTypescriptTypes.push(type);
        });
      });
      return (
        fieldTypescriptTypes.length > 0 &&
        fieldTypescriptTypes.every((val, i, arr) => val === arr[0]) &&
        fieldTypescriptTypes[0] === TypescriptTypeEnum.ANY
      );
    } else {
      return false;
    }
  }

  private applyCustomSortOnFinalArray(finalData: FunctionDefinitionModel[]) {
    const _nullParameters = finalData.filter((f) => f.parameters && f.parameters.length === 0);
    const _anyParameters = finalData.filter(
      (f) => f.parameters && f.parameters.length > 0 && f.parameters[0]?.typescript_type === TypescriptTypeEnum.ANY,
    );
    const _manyParameters = finalData.filter(
      (f) => f.parameters && f.parameters.filter((p) => p.typescript_type.split(' | ').length > 1).length > 0,
    );
    const functionNames = _nullParameters.map((a) => a.function_name);
    functionNames.push(..._anyParameters.map((a) => a.function_name));
    functionNames.push(..._manyParameters.map((a) => a.function_name));
    finalData = finalData.filter(function (item) {
      return functionNames.indexOf(item.function_name) === -1;
    });
    finalData.push(..._manyParameters);
    finalData.push(..._anyParameters);
    finalData.push(..._nullParameters);
    return finalData;
  }
}
